class CustomCartDrawer extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.bindEvents();
    this.connectCartIcon();
    requestAnimationFrame(() => {
      this.renderProgress();
      this.manageGiftProduct();
      this.scheduleGiftFade();
    });
    // Initial delivery message based on per-variant inventory
    this.updateDelivery();

    // On page load, fetch current cart to pick up any already-applied discount
    // codes that the server-side Liquid render didn't surface as coupon tags
    // (e.g. line-level automatic discounts).
    this._restoreDiscountCodes();
  }

  /* ---------- Restore discount codes from server cart on page load ---------- */
  _restoreDiscountCodes() {
    // If the server already rendered coupon tags, no need to restore
    if (this.querySelector('.ccd-coupon-tags-wrapper')) return;
    fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
    .then(r => r.json())
    .then(cart => {
      const codes = (cart.discount_codes || [])
        .filter(c => c.applicable !== false)
        .map(c => c.code);
      if (codes.length) {
        this._discountCodes = codes;
        this._injectCouponTags();
      }
    })
    .catch(() => {});
  }

  /* ---------- Events ---------- */
  bindEvents() {
    if (this._cb) this.removeEventListener('click', this._cb);
    this._cb = (e) => {
      const t = e.target.closest('[data-ccd-overlay],[data-ccd-close]');
      if (t) { e.preventDefault(); this.close(); return }
      const plus = e.target.closest('[data-ccd-plus]');
      if (plus) { e.preventDefault(); this.changeQty(plus.dataset.line, 1); return }
      const minus = e.target.closest('[data-ccd-minus]');
      if (minus) { e.preventDefault(); this.changeQty(minus.dataset.line, -1); return }
      const remove = e.target.closest('[data-ccd-remove]');
      if (remove) { e.preventDefault(); this.changeQty(remove.dataset.line, -999); return }
      const add = e.target.closest('[data-ccd-add]');
      if (add) { e.preventDefault(); this.addVariant(add.dataset.variant, add); return }
      const removeCoupon = e.target.closest('[data-ccd-remove-coupon]');
      if (removeCoupon) {
        e.preventDefault();
        this.removeCoupon();
        return;
      }
      // Apply coupon on button click — use direct match on the button
      if (e.target.closest('[data-ccd-apply-coupon]')) {
        e.preventDefault();
        const f = this.querySelector('[data-ccd-discount-form]');
        if (f) this.applyCoupon(f);
        return;
      }
    };
    this.addEventListener('click', this._cb);

    // Separate keydown handler for Enter on discount input
    if (this._kd) this.removeEventListener('keydown', this._kd);
    this._kd = (e) => {
      const inp = e.target.closest('[data-ccd-discount-form] input[name="discount"]');
      if (inp && e.key === 'Enter') { e.preventDefault(); const f = this.querySelector('[data-ccd-discount-form]'); if (f) this.applyCoupon(f); }
    };
    this.addEventListener('keydown', this._kd);

    // Change handler for upsell size dropdown
    if (this._us) this.removeEventListener('change', this._us);
    this._us = (e) => {
      const sel = e.target.closest('[data-ccd-upsell-select]');
      if (sel) { this.selectUpsellVariant(sel); return }
    };
    this.addEventListener('change', this._us);

    if (!this._esc) {
      document.addEventListener('keyup', (e) => { if (e.key === 'Escape' && this.classList.contains('active')) this.close(); });
      this._esc = true;
    }
  }

  /* ---------- Track applied discount codes client-side ---------- */
  _appliedCodes() {
    // Read codes from the rendered coupon tags (if any) and from our own
    // client-side set (in case the server didn't render them).
    const fromTags = Array.from(this.querySelectorAll('.ccd-coupon-tag__code'))
      .map(el => el.textContent.trim())
      .filter(Boolean);
    const fromProp = (this._discountCodes || []);
    // Merge both, de-duplicate case-insensitively
    const merged = [...fromTags];
    for (const c of fromProp) {
      if (!merged.some(m => m.toLowerCase() === c.toLowerCase())) merged.push(c);
    }
    return merged;
  }

  applyCoupon(f) {
    const inp = f.querySelector('input[name="discount"]');
    const code = inp ? inp.value.trim() : '';
    if (!code) { this.showToast('Bitte gib einen Rabattcode ein.'); return; }
    this.setLoading(true);

    // Preserve any already-applied codes from our tracked list
    const existing = this._appliedCodes();
    const all = existing.slice();
    if (!all.some(c => c.toLowerCase() === code.toLowerCase())) all.push(code);

    // Apply discount via /cart/update.js (Shopify Cart AJAX API — `discount`
    // takes a comma-separated string). Request the rendered sections in the SAME
    // call so we avoid a second round-trip.
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        discount: all.join(','),
        attributes: {
cart_drawer_coupon: code
},
        sections: ['custom-cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname
      })
    })
    .then(r => r.json())
.then(cart => {
      if (inp) inp.value = '';
      this.setLoading(false);
      const sectionHtml = cart.sections && cart.sections['custom-cart-drawer'];
      // A real Shopify discount code is valid if Shopify accepted it.
      const entry = (cart.discount_codes || []).find(c => c.code && c.code.toLowerCase() === code.toLowerCase());
      const shopifyAccepted = entry ? entry.applicable !== false : false;
      // The Teks Bundle app applies its own discount via the attribute, separate
      // from Shopify's discount engine — detect success by checking whether a
      // bundle/discount tag for this code was actually rendered on a line item.
      const bundleApplied = !!sectionHtml && sectionHtml.toLowerCase().includes(code.toLowerCase());
      const invalid = !shopifyAccepted && !bundleApplied;
      // Track applied codes client-side
      this._discountCodes = (cart.discount_codes || [])
        .filter(c => c.applicable !== false)
        .map(c => c.code);
      if (sectionHtml) {
        this.renderContents({ sections: cart.sections });
      }
      if (invalid) this.showModal('Rabattcode ungültig.');
    })
  }

  connectCartIcon() {
    const link = document.querySelector('#cart-link');
    if (!link || link.dataset.ccd) return;
    link.dataset.ccd = '1';
    link.addEventListener('click', (e) => { e.preventDefault(); this.open(link); });
  }

  /* ---------- Open / Close ---------- */
  open(el) {
    this.activeEl = el;
    this.classList.add('active');
    document.documentElement.classList.add('ccd-active');
    document.body.classList.add('ccd-open');
    const close = this.querySelector('.ccd__close');
    if (close) close.focus();
    this.renderProgress();
    this.updateDelivery();
    this.loadUpsells();
  }

  /* ---------- Upsells (fetched once, cached, re-injected on re-render) ---------- */
  loadUpsells() {
    // Already have them (or an in-flight request) — just (re)inject the cache.
    if (this._upsellHTML != null) { this.injectUpsells(); return; }
    if (this._upsellLoading) return;
    this._upsellLoading = true;
    fetch(`${window.location.pathname}?sections=cart-upsells`, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(r => r.json())
      .then(d => {
        this._upsellLoading = false;
        this._upsellHTML = (d && typeof d['cart-upsells'] === 'string') ? d['cart-upsells'].trim() : '';
        // If the response contains a <product-recommendations> element (from
        // Search & Discovery), its data-url points to the complementary products
        // endpoint. We need to fetch that URL to populate the actual product cards.
        if (this._upsellHTML) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(this._upsellHTML, 'text/html');
          const recEl = doc.querySelector('product-recommendations');
          if (recEl && recEl.dataset.url) {
            this._upsellRecUrl = recEl.dataset.url;
            // Fetch the recommendations endpoint — this returns the same section
            // but with recommendations.performed? = true and products populated.
            fetch(recEl.dataset.url)
              .then(r => r.text())
              .then(text => {
                const html = document.createElement('div');
                html.innerHTML = text;
                const populated = html.querySelector('product-recommendations');
                if (populated && populated.innerHTML.trim().length) {
                  this._upsellHTML = populated.innerHTML;
                }
                this.injectUpsells();
              })
              .catch(() => { this.injectUpsells(); });
            return;
          }
        }
        this.injectUpsells();
      })
      .catch(() => { this._upsellLoading = false; });
  }

  injectUpsells() {
    const slot = this.querySelector('[data-ccd-upsell]');
    if (!slot) return;
    const html = this._upsellHTML || '';
    slot.innerHTML = html;
    slot.hidden = !slot.querySelector('.ccd-upsell-card');
  }

  // Drop a card from the cache once its product is in the cart, so it doesn't
  // reappear on the next re-injection.
  removeUpsellFromCache(variantId) {
    if (!this._upsellHTML || !variantId) return;
    const doc = new DOMParser().parseFromString(`<div>${this._upsellHTML}</div>`, 'text/html');
    const wrap = doc.body.firstElementChild;
    const card = wrap.querySelector(`[data-ccd-upsell-card][data-variant="${variantId}"]`);
    if (card) card.remove();
    // If no cards remain, blank the whole block (heading + grid).
    this._upsellHTML = wrap.querySelector('.ccd-upsell-card') ? wrap.innerHTML.trim() : '';
  }

  close() {
    this.classList.remove('active');
    document.documentElement.classList.remove('ccd-active');
    document.body.classList.remove('ccd-open');
    if (this.activeEl) this.activeEl.focus();
  }

  /* ========== PROGRESS BAR ========== */
 renderProgress() {
    const g = this.dataset.giftEnabled === 'true' ? (+this.dataset.giftAmount || 0) : 0;
    const s = +this.dataset.shipAmount || 0;
    const total = +this.dataset.cartTotal || 0;  // in cents
    const giftEnabled = this.dataset.giftEnabled === 'true';
    const giftLabel = this.dataset.giftLabel || 'Gratis Gürtel';
    const gUnlocked = g > 0 && total >= g;
    const sUnlocked = s > 0 && total >= s;

    // Visual positions of nodes on the bar (must match Liquid template)
    const sVisualPos = giftEnabled ? 70 : 100;  // ship node visual %
    const gVisualPos = 100;                      // gift node visual %

    // Map real cart value to visual fill position
    // Between 0 and ship threshold → fill from 0 to sVisualPos
    // Between ship and gift threshold → fill from sVisualPos to gVisualPos
    // Map real cart value to visual fill position
    let greenFill = 0, blackFill = 0, blackLeft = 0;

    if (!giftEnabled) {
      // Only one milestone: ship at 100%
      greenFill = s > 0 ? Math.min(Math.round(total / s * 100), 100) : 0;
    } else {
      if (!sUnlocked) {
        // Before ship: green fills 0 → sVisualPos proportionally
        greenFill = s > 0 ? Math.min(Math.round((total / s) * sVisualPos), sVisualPos) : 0;
      } else if (sUnlocked && !gUnlocked) {
        // Ship unlocked, gift not yet — green continues proportionally past ship node
        const range = g - s;
        const progress = total - s;
        const remaining = gVisualPos - sVisualPos;
        greenFill = range > 0 ? sVisualPos + Math.min(Math.round((progress / range) * remaining), remaining) : sVisualPos;
        blackFill = 0;
        blackLeft = 0;
      } else if (sUnlocked && gUnlocked) {
        // Both unlocked
        greenFill = 100;
        blackFill = 0;
      }
    }

    const grn = this.querySelector('[data-ccd-fill-green]');
    const blk = this.querySelector('[data-ccd-fill-black]');
    if (grn) { grn.style.width = greenFill + '%'; }
    if (blk) { blk.style.width = blackFill + '%'; blk.style.left = blackLeft + '%'; }

    // Dot unlock
  const sNode = this.querySelector('[data-ccd-node-ship]');
    if (sNode) {
      const dot = sNode.querySelector('.ccd-node__dot');
      if (dot) dot.classList.toggle('is-unlocked', sUnlocked);
      const sub = sNode.querySelector('.ccd-node__sub');
      if (sub) sub.textContent = sUnlocked ? 'freigeschaltet' : this.$(s);
    }
    const gNode = this.querySelector('[data-ccd-node-gift]');
    if (gNode) {
      const dot = gNode.querySelector('.ccd-node__dot');
      if (dot) dot.classList.toggle('is-unlocked', gUnlocked);
      const sub = gNode.querySelector('.ccd-node__sub');
      if (sub) sub.textContent = gUnlocked ? 'freigeschaltet' : this.$(g);
    }

    // Reward message
    const rw = this.querySelector('[data-ccd-reward]');
    const rt = this.querySelector('[data-ccd-reward-text]');
   if (giftEnabled && gUnlocked && sUnlocked) {
        rw.className = 'ccd-reward is-complete';
        rt.textContent = giftLabel + ' & kostenloser Versand freigeschaltet';
      } else if (!sUnlocked && s > 0) {
        rw.className = 'ccd-reward';
        rt.innerHTML = 'Nur noch <strong>' + this.$(s - total) + '</strong> bis zum kostenlosen Versand';
      } else if (giftEnabled && !gUnlocked && g > 0) {
        rw.className = 'ccd-reward';
        rt.innerHTML = 'Nur noch <strong>' + this.$(g - total) + '</strong> bis zu deinen ' + giftLabel.replace(/&/g, '&amp;').replace(/</g, '&lt;');
      } else if (sUnlocked) {
        rw.className = 'ccd-reward is-complete';
        rt.textContent = 'Kostenloser Versand freigeschaltet';
      }
  }

  $(cents) { return (cents / 100).toFixed(2).replace('.', ',') + ' €'; }

  /* ---------- Cart mutations ---------- */
  changeQty(line, delta) {
    const item = this.querySelector(`[data-ccd-item][data-line="${line}"]`);
    if (!item) return;
    // If user is manually removing the gift item, remember their choice
    if (item.hasAttribute('data-ccd-gift-item') && delta === -999) {
      this._giftDeclined = true;
    }
    const qtyEl = item.querySelector('.ccd-qty__value');
    const qty = Math.max(0, (parseInt(qtyEl ? qtyEl.textContent : '1') || 1) + delta);
    // Optimistic qty display update
    if (qtyEl && qty > 0) qtyEl.textContent = qty;
    const doFetch = () => {
      this.setLoading(true);
      fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ line: +line, quantity: qty, sections: ['custom-cart-drawer','cart-icon-bubble'], sections_url: window.location.pathname })
      }).then(r => r.json()).then(d => this.renderContents(d)).catch(console.error).finally(() => this.setLoading(false));
    };
    if (qty === 0) {
      item.classList.add('is-removing');
      setTimeout(doFetch, 180);
    } else {
      doFetch();
    }
  }

  /* ---------- Google Sheets click tracker ---------- */
  _trackClick(tab, product, price) {
    var url = 'https://script.google.com/macros/s/AKfycbzS12Qgk5OQdM8KevS3pKE6Ro5ur4Ht5aMQNFrLp5Q-_te_2wWyGUQeccG7z8ePKGt6pg/exec';
    try {
      fetch(url, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({ tab: tab, product: product || '', price: price || '', qty: 1 })
      });
    } catch (e) {}
  }

  addVariant(id, btn) {
    if (!id) return;
    // Track upsell click (Upsell Cart tab) — does not affect add-to-cart flow
    if (btn) {
      var card = btn.closest('[data-ccd-upsell-card]');
      var title = card ? card.querySelector('.ccd-upsell-card__title') : null;
      var priceEl = card ? card.querySelector('.ccd-upsell-card__price') : null;
      this._trackClick('Upsell Cart', title ? title.textContent.trim() : '', priceEl ? priceEl.textContent.trim() : '');
    }
    if (btn) btn.classList.add('is-loading');
    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: +id, quantity: 1 }], sections: ['custom-cart-drawer','cart-icon-bubble'], sections_url: window.location.pathname })
    }).then(r => r.json()).then(d => {
      if (d.status) return;
      // Clear cached upsells so they re-fetch with the updated cart
      this._upsellHTML = null;
      this._upsellLoading = false;
      this._upsellRecUrl = null;
      this.renderContents(d);
    }).catch(console.error).finally(() => { if (btn) btn.classList.remove('is-loading'); });
  }

  /* ---------- Upsell size dropdown ---------- */
  selectUpsellVariant(sel) {
    const card = sel.closest('[data-ccd-upsell-card]');
    if (!card) return;
    const variantId = sel.value;
    if (!variantId) return;
    // Update both the card and the add button
    card.dataset.variant = variantId;
    const addBtn = card.querySelector('[data-ccd-add]');
    if (addBtn) {
      addBtn.dataset.variant = variantId;
    }
  }

  /* ---------- Delivery message (1-3 / 1-7 days) ---------- */
  updateDelivery() {
    const el = this.querySelector('[data-ccd-delivery-text]');
    if (!el) return;
    const fast = this.dataset.deliveryFast || 'Lieferung in 1–3 Werktagen';
    const oversell = this.dataset.deliveryOversell || 'Lieferung in 3–9 Werktagen';

    // Read per-variant inventory from the embedded JSON script
    let invData = {};
    try {
      const script = this.querySelector('#CartInventoryData');
      if (script) invData = JSON.parse(script.textContent);
    } catch(e) {}

    // Check each cart item row — only use oversell when a variant is actually
    // out of stock (qty <= 0) but still sellable via the "continue" policy.
    // Real stock (qty > 0) always shows the fast message, even if "continue" is on.
    let hasOversell = false;
    const items = this.querySelectorAll('[data-ccd-item]');
    if (items.length) {
      for (const item of items) {
        const vid = item.dataset.variantId;
        if (!vid) continue;
        const v = invData[vid];
        if (v && v.policy === 'continue' && (v.qty || 0) <= 0) {
          hasOversell = true;
          break;
        }
      }
    }

    el.textContent = hasOversell ? oversell : fast;
  }

  /* ---------- Gift product management ---------- */
  manageGiftProduct() {
    // Only run when a gift product is configured in the theme customizer
    if (this.dataset.giftEnabled !== 'true') return;
    if (this._managingGift) return;
    const giftVariantId = +this.dataset.giftVariant;
    if (!giftVariantId) return;

    const g = +this.dataset.giftAmount || 0;
    const total = +this.dataset.cartTotal || 0;
    const gUnlocked = g > 0 && total >= g;
    const giftEl = this.querySelector('[data-ccd-gift-item]');
    const giftInCart = !!giftEl;

    if (!gUnlocked) {
      // Reset declined flag so gift auto-adds again next time threshold is crossed
      this._giftDeclined = false;
      this._prevGiftUnlocked = false;
      if (giftInCart) {
        // Auto-remove gift when cart drops below threshold
        const line = giftEl.dataset.line;
        this._managingGift = true;
        // Optimistically fade the gift out and block checkout for the duration
        // of the removal round-trip, so the cart can't be checked out with only
        // the free gift while it is still being removed.
        giftEl.classList.add('is-removing');
        this.setLoading(true);
        fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ line: +line, quantity: 0, sections: ['custom-cart-drawer','cart-icon-bubble'], sections_url: window.location.pathname })
        }).then(r => r.json())
          .then(d => { this._managingGift = false; this.renderContents(d); this.setLoading(false); })
          .catch(() => { this._managingGift = false; this.setLoading(false); });
      }
      return;
    }

    // Threshold met — auto-add gift if not already present and user hasn't declined
    this._prevGiftUnlocked = true;
    if (giftInCart || this._giftDeclined) return;

    this._managingGift = true;
    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        items: [{ id: giftVariantId, quantity: 1, properties: { _gift: 'true' } }],
        sections: ['custom-cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname
      })
    }).then(r => r.json())
      .then(d => { this._managingGift = false; if (!d.status) this.renderContents(d); })
      .catch(() => { this._managingGift = false; });
  }

  /* Keep the gift's green highlight for a few seconds, then settle it to the
     normal item background so it doesn't permanently dominate the cart. */
  scheduleGiftFade() {
    if (this.dataset.giftEnabled !== 'true') return;
    if (this._giftFadeTimer) { clearTimeout(this._giftFadeTimer); this._giftFadeTimer = null; }
    if (!this.querySelector('[data-ccd-gift-item]')) return;
    this._giftFadeTimer = setTimeout(() => {
      const g = this.querySelector('[data-ccd-gift-item]');
      if (g) g.classList.add('is-gift-settled');
    }, 3500);
  }

  /* ---------- Render ---------- */
  renderContents(parsed) {
    if (!parsed || !parsed.sections) return;

    // Update the cart total from the response BEFORE re-rendering
    // The response includes the full section HTML with updated data-cart-total
    const sectionHtml = parsed.sections['custom-cart-drawer'];
    if (sectionHtml) {
      const m = sectionHtml.match(/data-cart-total="(\d+)"/);
      if (m) this.dataset.cartTotal = m[1];
      // Also update gift/ship amounts in case they changed
      const gm = sectionHtml.match(/data-gift-amount="(\d+)"/);
      if (gm) this.dataset.giftAmount = gm[1];
      const sm = sectionHtml.match(/data-ship-amount="(\d+)"/);
      if (sm) this.dataset.shipAmount = sm[1];
    }

    // Check if the cart is empty by looking for the empty-state class in the response
    const isEmpty = sectionHtml && sectionHtml.includes('ccd--empty');
    this.classList.toggle('is-empty', isEmpty);

    // Re-render the drawer content
    const target = document.querySelector('#CustomCartDrawer');
    if (target && sectionHtml) {
      const parsedHTML = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const inner = parsedHTML.querySelector('#CustomCartDrawer');
      if (inner) target.innerHTML = inner.innerHTML;

      // Also update the #CartInventoryData script (it's a sibling of #CustomCartDrawer,
      // so it isn't included in the innerHTML swap above)
      const newInvScript = parsedHTML.querySelector('#CartInventoryData');
      const oldInvScript = this.querySelector('#CartInventoryData');
      if (newInvScript && oldInvScript) {
        oldInvScript.textContent = newInvScript.textContent;
      }
    }

    // Re-render cart icon bubble. The 'cart-icon-bubble' section renders only
    // the INNER count node (.hdr-cart-count-numb), while the header element
    // #cart-icon-bubble is the wrapper. Replace innerHTML (not outerHTML) so the
    // wrapper — and its id — survive and update on every cart change.
    const bubbleHtml = parsed.sections['cart-icon-bubble'];
    if (typeof bubbleHtml === 'string') {
      const bubbleTarget = document.getElementById('cart-icon-bubble');
      if (bubbleTarget) bubbleTarget.innerHTML = bubbleHtml.trim();
    }

    // Sync discount codes from the server-rendered HTML. If the server rendered
    // coupon tags, those are authoritative. If the cart is empty, clear codes.
    // This prevents stale codes from persisting across cart empty/refill cycles.
    this._syncDiscountCodes(sectionHtml);

    // If we have client-side tracked discount codes but the server didn't render
    // coupon tags (e.g. line-level discounts that Liquid doesn't surface), inject
    // them so the user can still remove the code.
    this._injectCouponTags();

    this.bindEvents();
    // Re-inject cached upsells or re-fetch if cache was cleared (e.g. after adding a product)
    if (this._upsellHTML != null) {
      this.injectUpsells();
    } else {
      this.loadUpsells();
    }
    if (!this.classList.contains('active')) this.open();
    requestAnimationFrame(() => { this.renderProgress(); this.updateDelivery(); this.manageGiftProduct(); this.scheduleGiftFade(); });
  }

  /* ---------- Sync discount codes from server-rendered HTML ---------- */
  _syncDiscountCodes(sectionHtml) {
    if (!sectionHtml) return;
    // Parse the server-rendered coupon tag codes from the HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(sectionHtml, 'text/html');
    const tagCodes = Array.from(doc.querySelectorAll('.ccd-coupon-tag__code'))
      .map(el => el.textContent.trim())
      .filter(Boolean);
    if (tagCodes.length) {
      // Server rendered cart-level coupon tags — use those (they're authoritative)
      this._discountCodes = tagCodes;
    } else if (sectionHtml.includes('ccd--empty')) {
      // Cart is empty — clear stale codes
      this._discountCodes = [];
    } else if (sectionHtml.includes('ccd-item__discount-tag')) {
      // No cart-level tags, but there ARE line-level discounts (automatic
      // discounts, bundle discounts, etc.). Show a generic remove option.
      // Use the discount title from the first line-level tag as the display code.
      const firstTitle = doc.querySelector('.ccd-item__discount-name');
      const label = firstTitle ? firstTitle.textContent.trim() : 'Rabatt';
      this._discountCodes = [label];
    } else {
      // Non-empty cart, no discounts at all — clear codes
      this._discountCodes = [];
    }
  }
  /* ---------- Inject coupon tags client-side ---------- */
  _injectCouponTags() {
    const codes = this._discountCodes || [];
    if (!codes.length) return;
    // Only inject if the server didn't already render them
    if (this.querySelector('.ccd-coupon-tags-wrapper')) return;
    const container = this.querySelector('.ccd-discount-container');
    if (!container) return;
    const wrapper = document.createElement('div');
    wrapper.className = 'ccd-coupon-tags-wrapper';
    wrapper.innerHTML = codes.map(code => `
      <div class="ccd-coupon-tag" data-ccd-coupon-tag>
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M20.59 13.41 11 3.83A2 2 0 0 0 9.59 3H4a1 1 0 0 0-1 1v5.59A2 2 0 0 0 3.83 11l9.58 9.59a2 2 0 0 0 2.83 0l4.35-4.35a2 2 0 0 0 0-2.83Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><circle cx="7.5" cy="7.5" r="1.2" fill="currentColor"/></svg>
        <span class="ccd-coupon-tag__code">${this._escapeHtml(code)}</span>
        <button class="ccd-coupon-tag__remove" type="button" data-ccd-remove-coupon aria-label="Coupon entfernen">×</button>
      </div>
    `).join('');
    container.appendChild(wrapper);
  }

  _escapeHtml(str) {
    const d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }

  /* ---------- Remove coupon ---------- */
  removeCoupon() {
    this.setLoading(true);
    // Clear our client-side tracking
    this._discountCodes = [];

    // Remove all discounts via /cart/update.js (empty `discount` string clears
    // them). Request the rendered sections in the SAME call — one round-trip.
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        discount: '',
        attributes: {
cart_drawer_coupon: ''
},
        sections: ['custom-cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname
      })
    })
    .then(r => r.json())
    .then(cart => {
      this.setLoading(false);
      if (cart.sections && cart.sections['custom-cart-drawer']) {
        this.renderContents({ sections: cart.sections });
      }
    })
    .catch(e => {
      this.setLoading(false);
      console.error('Discount remove error:', e);
    });
  }

  /* ---------- Modal popup ---------- */
  showModal(msg) {
    const old = this.querySelector('.ccd-modal-backdrop');
    if (old) old.remove();

    const b = document.createElement('div');
    b.className = 'ccd-modal-backdrop';
    b.innerHTML = '<div class="ccd-modal"><p class="ccd-modal__msg"></p><button class="ccd-modal__btn">OK</button></div>';
    b.querySelector('.ccd-modal__msg').textContent = msg;
    b.addEventListener('click', (e) => { if (e.target === b || e.target.classList.contains('ccd-modal__btn')) b.remove(); });
    document.addEventListener('keyup', function _h(ev) { if (ev.key === 'Escape') { b.remove(); document.removeEventListener('keyup', _h); } });
    this.querySelector('.ccd-container').appendChild(b);
  }

  /* ---------- Inline toast ---------- */
  showToast(msg) {
    const old = this.querySelector('.ccd-toast');
    if (old) old.remove();
    const t = document.createElement('div');
    t.className = 'ccd-toast';
    t.textContent = msg;
    this.querySelector('.ccd-container').appendChild(t);
    setTimeout(() => t.remove(), 2500);
  }

  setLoading(on) { this.classList.toggle('ccd--loading', on); }

  getSectionsToRender() {
    return [{ id: 'custom-cart-drawer' }, { id: 'cart-icon-bubble' }];
  }

  setActiveElement(el) { this.activeEl = el; }
}

customElements.define('custom-cart-drawer', CustomCartDrawer);
